import React, { Fragment, useEffect, useRef, useState, useCallback, useMemo } from "react";
import Layout from "../../components/biblio/SuiviLayout";
import {
  dateFormat,
  getAuthToken,
  setTitle,
} from "../../helpers/Helper";
import { SITE_URL, API_SUIVI_URL } from "../../helpers/Constants";
import Modal from "../../components/Modal";
import UserStore from "../../store/UserStore";
import Loading from "../../helpers/Loading";
import Select from 'react-select';
import Swal from "sweetalert2";
import { Link } from 'react-router-dom'
import { notify } from "../../helpers/Helper";
import SecureFileViewer from './utils/SecureFileViewer';
import { Trash, Pencil,  } from "../../components/Icons";

/**
 * ---- helpers existants / adaptés ----
 */

const apiBase = (API_SUIVI_URL || "").replace(/\/$/, "");

function getSuiviToken() {
  try {
    const raw = localStorage.getItem("bib2z_token_SUIVI");
    return raw ? raw.replace(/^"(.*)"$/, "$1") : null;
  } catch {
    return null;
  }
}
function authHeaders(extra = {}) {
  const token = getSuiviToken();
  return {
    Accept: "application/json",
    ...(extra || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

// IRIs attendus par le backend
const isIri = (v) => typeof v === "string" && (v.includes("/api/") || v.startsWith("/"));

const iri = {
  typeAudit: (id) => (isIri(id) ? id : `/api/type_audits/${id}`),
  audit: (id) => (isIri(id) ? id : `/api/audits/${id}`),
  typeRevue: (id) => (isIri(id) ? id : `/api/type_revues/${id}`),
  structure: (id) => (isIri(id) ? id : `/api/structures/${id}`),
  localite: (id) => (isIri(id) ? id : `/api/localites/${id}`),
  media: (id) => (isIri(id) ? id : `/api/media/${id}`),
  programme: (id) => (isIri(id) ? id : `/api/programmes/${id}`),
  personne: (id) => (isIri(id) ? id : `/api/personnes/${id}`),
  user: (id) => (isIri(id) ? id : `/api/users/${id}`),
};

function toAbsoluteUrl(pathOrFull) {
  pathOrFull = pathOrFull.replace("/telechargements", "/media/download").replace("/api/media/download", "/media/download");
  if (!pathOrFull) return null;
  if (pathOrFull.startsWith("http")) return pathOrFull;
  return `${(API_SUIVI_URL || "").replace(/\/$/, "")}${pathOrFull}`;
}

function buildPagination(currentPage, perPage, total) {
  const last = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(Math.max(Number(currentPage) || 1, 1), last);
  const from = total === 0 ? 0 : (current - 1) * perPage + 1;
  const to = Math.min(total, current * perPage);
  const prevPage = current > 1 ? current - 1 : null;
  const nextPage = current < last ? current + 1 : null;

  return {
    page: current,
    currentPage: current,
    current_page: current,
    perPage,
    per_page: perPage,
    total,
    firstPage: 1,
    first_page: 1,
    prevPage,
    prev_page: prevPage,
    previous: prevPage,
    nextPage,
    next_page: nextPage,
    lastPage: last,
    last_page: last,
    from,
    to,
    hasPrev: !!prevPage,
    hasNext: !!nextPage,
  };
}

/**
 * ---- composant principal ----
 */

export default function ProjetProgramme() {
  // table / pagination UI —> pilotés côté API
  const endpoint = "programmes";
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(buildPagination(1, 10, 0));
  const [loading, setLoading] = useState(true);

  // tri côté API
  const [orderCreatedAt, setOrderCreatedAt] = useState('desc'); // défaut: récents d'abord
  const [mouseOver, setMouseOver] = useState(null);

  // viewer PDF
  const [viewerSource, setViewerSource] = useState(null); // string | array
  const [showViewer, setShowViewer] = useState(false);
  const token = getAuthToken();

  // Filtres / search (stocke des objets d'option {value,label} pour react-select)
  const [search, setSearch] = useState("");
 
  // Modal formulaire
  const [formOpen, setFormOpen] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [formDraft, setFormDraft] = useState({ title: "", type: "" });
  const [activeRecord, setActiveRecord] = useState(null);
  
  const openFormModal = (record) => {
    setActiveRecord(record);
    setFormDraft(record ? { title: record.title, type: record.type } : { title: "", type: "" });
    setFormOpen(true);
  };
  const closeFormModal = () => setFormOpen(false);
  
  const canSaveForm = useMemo(
    () => typeof formDraft?.title === "string" && formDraft.title.trim().length > 0,
    [formDraft]
  );

  const saveForm = async (draft) => {
    try {
      setFormSaving(true);
      const payload = {
        type: draft.type || "",
        title: draft.title?.trim() || "",
      };

      const slug = activeRecord ? '/'+activeRecord.id : "";

      const res = await fetch(apiBase + '/' + 'programmes' + slug , {
        method: activeRecord ? "PATCH" : "POST",
        headers: { ...authHeaders({ "Content-Type": activeRecord ? "application/merge-patch+json" : "application/json" }) },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        notify("Erreur", "Échec de l'enregistrement.", "error");
        return;
      }
      notify("Succès", "Enregistrement effectué.", "success");
      setFormOpen(false);
      fetchRecords();
    } catch {
      notify("Erreur", "Impossible d'enregistrer le formulaire.", "error");
    } finally {
      setFormSaving(false);
    }
  };

  // Confirmation et suppression
  const confirmAndRemoveRecord = (idx) => {
    // swal2 peut être utilisé ici pour une meilleure UI
    Swal.fire({
      title: "Faut-il vraiment supprimer cet enregistrement ?",
      showCancelButton: true,
      confirmButtonColor: "#019264",
      confirmButtonText: "Confirmer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await fetch(`${apiBase}/${endpoint}/${idx}`, {
          method: "DELETE",
          headers: authHeaders(),
        });
        notify("Succès", "Enregistrement supprimé.", "success");
        fetchRecords();
      }
    });
  };

  setTitle("RAPPORTS DE SUIVI ET RECOMMANDATION");

  /**
   * buildQueryString:
   * Construction d'une query *API-first* (pagination/tri/filters)
   * Correspond à l'exemple fourni :
   */
  const buildQueryString = useCallback((override = {}) => {
    const params = new URLSearchParams();

    const p = {
      page,
      perPage,
      search,
      orderCreatedAt,
      ...override,
    };

    // Pagination
    params.set("page", String(p.page || 1));
    params.set("itemsPerPage", String(p.perPage || 10));
    params.set("pagination", "true");

    // Filtres (adapter si vos champs exacts diffèrent)
    if (p.search) params.append("mission", p.search); // backend: mission contient la recherche

    // Tri
    // if (p.orderCreatedAt) params.append("order[createdAt]", p.orderCreatedAt);
    
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [
    page,
    perPage,
    search,
    orderCreatedAt,
  ]);

  /**
   * fetchRecords (API-first)
   * - Utilise pagination & tri côté serveur
   * - Calcule la pagination à partir de hydra:totalItems si présent
   */
  const fetchRecords = useCallback(async (pageOverride = null) => {
    setLoading(true);
    // alert(page);
    try {
      await UserStore.getCurrentUser().catch(() => {});
      const tokenSuivi = getSuiviToken();
      const slug = endpoint;
      const qs = buildQueryString(pageOverride ? { page: pageOverride } : undefined);
      const url = `${API_SUIVI_URL.replace(/\/$/, "")}/${slug}${qs}`;

      const res = await fetch(url, {
        headers: {
          Accept: "application/ld+json",
          ...(tokenSuivi ? { Authorization: `Bearer ${tokenSuivi}` } : {}),
        },
      });

      if (!res.ok) {
        console.error("Erreur API", res.status);
        setRecords([]);
        setPagination(buildPagination(pageOverride || page, perPage, 0));
      } else {
        const raw = await res.json();
        let list;
        
        if (Array.isArray(raw)) {
          list = raw;
        } else if (raw && Array.isArray(raw["member"])) {
          list = raw["member"];
        } else {
          list = [];
        }

        // totalItems si fourni par API Platform
        const totalItems = Number(raw?.["hydra:totalItems"] ?? raw?.totalItems ?? list.length) || 0;

        setRecords(list);
        setPagination(buildPagination(pageOverride || page, perPage, totalItems));
      }
    } catch (e) {
      console.error(e);
      setRecords([]);
      setPagination(buildPagination(pageOverride || page, perPage, 0));
    } finally {
      setLoading(false);
    }
  }, [buildQueryString, page, perPage]);

  /** Premier chargement */
  useEffect(() => {
    fetchRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRecords]);

  /** Refetch quand perPage change (reset page=1) */
  useEffect(() => {
    setPage(1);
    fetchRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  /** Refetch quand tri change (conserve page courante) */
  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderCreatedAt]);

  /**
   * Refetch dès qu’un filtre change (UX dynamique)
   * Reset à page=1 pour éviter des pages vides
   */
  useEffect(() => {
    setPage(1);
    fetchRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  /** Handlers pagination : pas de <a href="/"> => pas de reload ni perte de focus */
  const goFirst = () => { if (pagination.firstPage !== pagination.currentPage) { setPage(pagination.firstPage); fetchRecords(pagination.firstPage); } };
  const goPrev  = () => { if (pagination.prevPage)  { setPage(pagination.prevPage);  fetchRecords(pagination.prevPage);  } };
  const goNext  = () => { if (pagination.nextPage)  { console.log(pagination.nextPage,'pagination.nextPage'); setPage(pagination.nextPage);  fetchRecords(pagination.nextPage);  } };
  const goLast  = () => { if (pagination.lastPage !== pagination.currentPage) { setPage(pagination.lastPage); fetchRecords(pagination.lastPage); } };

  /** Reset filtres */
  const resetFilters = () => {
    setSearch("");
    setOrderCreatedAt('desc');
    setPage(1);
    fetchRecords(1);
  };

  /**
   * corps principal
   */
    return (
      <Layout>

          <div>
            <div className="content-body">
              <div className="d-flex justify-content-between align-items-center my-3">
                <div>
                  <h4 style={{ textTransform: "uppercase" }}>Liste des projets/programmes</h4>
                </div>

                <button onClick={()=>{openFormModal(null)}} className="page-title" style={{ border: "none" }}>
                  Ajouter un projet/programme
                </button>
              </div>

              {/* Barre de filtres */}
              {/* <form
                className="card p-3 mb-3"
                style={{ backgroundColor: "#fff", border: "1px solid #ddd",marginRight: "10px",marginLeft: "5px" }}
                onSubmit={(e)=>e.preventDefault()} // pas de submit => pas de reload
              >
                <div className="mb-2" style={{ fontSize: "0.9rem", color: "#6c757d" }}>
                  Utiliser le filtre afin de vite retrouver un enregistrement.
                </div>

                <div className="row g-2 align-items-end">
                  <div className="col-md-9 col-lg-10">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fa fa-search" />
                      </span>
                      <input
                        name="title"
                        className="form-control"
                        placeholder="Rechercher..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-md-3 col-lg-2 d-flex gap-2 actions-bar">
                    <button
                      type="button"
                      className="btn btn-secondary w-100"
                      style={{ whiteSpace: "nowrap", float: "right" }}
                      onClick={resetFilters}
                      disabled={loading}
                    >
                      Réinitialiser
                    </button>
                  </div>
                </div>
              </form> */}

              <div className="container-fluid mt-3 mb-3">
                <div className="row mt-3">
                  <div className="col-sm-12 col-md-12 p-2">
                    <table className="table table-bordered table-striped">
                      <thead
                        style={{ textTransform: "uppercase", color: "#fff" }}
                        className="bckColorDark"
                      >
                        <tr>
                          <th>N°</th>
                          <th>Type</th>
                          <th>Titre</th>
                          <th><span style={{float: "right"}}>Actions</span></th>
                        </tr>
                      </thead>

                      <tbody>
                        {records && records.map((record, idx) => (
                          <tr key={record.id ?? idx}>
                            <td>
                              {pagination.from ? pagination.from + idx : idx + 1}
                            </td>

                            
                            <td style={{textTransform: "uppercase"}}>
                              { record.type }
                            </td>

                            <td>
                              <strong>
                                {record.title}
                              </strong>
                            </td>

                            <td>
                              <div style={{position:"relative", display:"inline-block", float: "right"}}>
                              <button
                                type="button"
                                title="Modifier"
                                className="btn btn-sm mr-2"
                                style={{marginRight: '10px', boxShadow: "none"}}
                                onClick={() => {openFormModal(record)}}
                              >
                                <Pencil />
                              </button>
                              <button 
                                title="Supprimer" 
                                type="button" 
                                className="btn btn-sm" 
                                style={{boxShadow: "none"}} 
                                onClick={() => confirmAndRemoveRecord(record.id)}
                              >
                                <Trash />
                              </button>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {!records?.length && (
                          <tr>
                            <td colSpan={6} className="text-center">
                              Aucun résultat
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination SPA : boutons => pas de reload ni perte de focus */}
                    <div>
                      <nav aria-label="" className=" mt-3">
                        <ul className="pagination w-25 mx-auto">
                          <li className={pagination.hasPrev ? 'page-item' : 'page-item disabled'}>
                            <button type="button" className="page-link" onClick={goFirst} aria-disabled={!pagination.hasPrev}>
                              <i className="fas fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={pagination.hasPrev ? 'page-item' : 'page-item disabled'}>
                            <button type="button" className="page-link" onClick={goPrev}>Précédent</button>
                          </li>
                          <li className={pagination.hasNext ? 'page-item' : 'page-item disabled'}>
                            <button type="button" className="page-link" onClick={goNext}>Suivant</button>
                          </li>
                          <li className={pagination.hasNext ? 'page-item' : 'page-item disabled'}>
                            <button type="button" className="page-link" onClick={goLast}>
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>

                    <div className="d-flex justify-content-end mt-2">
                      <select
                        className="form-control form-control-sm"
                        style={{ width: 120 }}
                        value={perPage}
                        onChange={(e) => setPerPage(Number(e.target.value) || 10)}
                      >
                        {[5, 10, 20, 50].map((n) => (
                          <option key={n} value={n}>
                            {n}/page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal formulaire */}
              <Modal
                show={formOpen}
                large={false}
                size="modal-md"
                title={!activeRecord ? "Ajouter un enregistrement" : "Modifier \"" + activeRecord?.title + "\""}
                body={<>
                    <span>
                      <div className="form-group mt-3" id="form-modal-body">
                        <label className="mb-2  label">Type *</label>
                        <select
                          name="type"
                          className="form-control"
                          value={formDraft?.type || ""}
                          onChange={(e) => {
                            setFormDraft({ ...formDraft, type: e.target.value});
                          }}
                        >
                          <option value="">Choisir…</option>
                          <option value="project">Projet</option>
                          <option value="program">Programme</option>
                        </select>
                        <label className="mb-2  label">Titre *</label>
                        <input
                          type="text"
                          className="form-control mb-2"
                          name="title"
                          value={formDraft?.title || ""}
                          onChange={(e) => {
                            setFormDraft({...formDraft, title: e.target.value});
                          }}
                        />
                      </div>
                      <div className="modal-footer text-center">
                        <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                          Annuler
                        </button>
                        <button
                          type="button"
                          className="btn btn-success"
                          disabled={!canSaveForm || formSaving}
                          onClick={() => saveForm?.(formDraft)}
                        >
                          {formSaving ? "Envoi..." : "Enregistrer"}
                        </button>
                      </div>
                    </span>
                  </>}
                onClose={closeFormModal}
              />
              <Modal
                large
                size="modal-lg"
                show={showViewer && !!viewerSource}
                title="Lecture du Document"
                body={<SecureFileViewer source={viewerSource} height={700} />}
                onClose={() => { setShowViewer(false); setViewerSource(null); }}
              />
            </div>
          </div>
      </Layout>
    );
}
